import { PosType } from './api/location.types';
import { vDriveDeliveryStatus } from './api/wallet.types';
import { Brand, SimpleBrandEntity } from './brand.types';
import {
  Coordinates,
  DeliveryCreationType,
  PhoneObject,
  Provider,
  VDriveProvider,
} from './shared.types';
import { SimpleUserEntity, User, UserRole } from './user.types';

export type CustomerInfo = {
  name: string;
  phone: string;
  phoneObject: PhoneObject;
};

export type DriverInfo = {
  name: string;
  phone: string;
  phoneObject: PhoneObject;
  phoneNumberForMerchant: string;
  phoneNumberForMerchantObject: PhoneObject;
  phoneNumberForCustomer: string;
  phoneNumberForCustomerObject: PhoneObject;
  pinCodeForMerchant: string;
  profilePicture: number | null;
  provider: VDriveProvider | null;
  location: Coordinates;
  /**
   * Tracking pages only: the number for whoever opened the link - the
   * customer's line, or with `source=merchant` the merchant's and its PIN
   * (only some fleets have one). Null until the driver has a number; absent
   * on a backend older than it.
   */
  contact?: {
    phone: { number: string; nationalNumber: string; isUSNumber: boolean };
    pinCode: string | null;
  } | null;
} | null;

export type VDriveOffer = {
  id: string;
  price: number;
  provider: VDriveProvider;
  distance: number;
  deliveryMinutes: number;
  pickupMinutes: number;
  tip?: number;
};

export type PickupInfo = {
  address: string;
  name: string;
  instructions: string | null;
};

export type DropoffInfo = {
  address: string;
  suite: string | null;
  instructions: string | null;
};

export type Charges = {
  tips: number;
  dspFee: number;
  deliveryFee: number;
  totalAmount: number;
  /**
   * Charged on this delivery under the location's debt repayment plan, cents;
   * 0 when nothing was. Not part of `totalAmount`. Missing before Oct 7, 2026.
   */
  debtRepayment?: number;
};

export type Timestamps = {
  pickup_estimate: string;
  dropoff_estimate: string;
  created_at: string;
  runner_assigned_at: string | null;
  runner_accepted_at: string | null;
  runner_arrived_origin: string | null;
  ready_at: string | null;
  runner_picked_up: string | null;
  runner_arrived_dropoff: string | null;
  runner_dropped_off: string | null;
  cancelled_at: string | null;
};

export type VDriveDeliveryEntity = {
  internalNotes: VDriveDeliveryInternalNoteEntity[] | null;
  orderId: number;
  id: number;
  source: Provider;
  provider: VDriveProvider | null;
  dspId: string | null;
  dspCustomerId: string | null;
  status: vDriveDeliveryStatus;
  customer: CustomerInfo;
  driver: DriverInfo;
  dropoffCoordinates: Coordinates;
  pickupCoordinates: Coordinates;
  pickupInfo: PickupInfo;
  dropoffInfo: DropoffInfo;
  timeZone: string;
  charges: Charges;
  timestamps: Timestamps;
  trackingLink: string | null;
  /**
   * The customer's support chat on this delivery's tracking link, for the
   * admin's "Open chat". Null when the customer never wrote; absent on a
   * backend older than the Dispatcher chat.
   */
  conversationId?: number | null;
  distanceMiles: number;
  /** What the DSP reported; admin only, display only. */
  dspDistanceMiles?: number | null;
  creationType: DeliveryCreationType;
  specialOfferPhoneNumber?: string | null;
  orderPlacedAt: string;
  // TODO DELIVERIES MUST
  address: string;
  instructions: string;

  vDriveSource: VDriveSource;
  brand: Brand;
  driverRating?: DriverRating;
  dispatcher?: Dispatcher | null;
  createdBy?: User;
  interceptorCallId?: string | null;
  /**
   * Backend guarantee that the call recording endpoint will answer. Only true
   * when both the interceptor call id and the linked call record exist -
   * interceptorCallId alone is not enough and still yields a 400.
   */
  hasCallRecording?: boolean;
  cancellationReason?: string | null;
  canMarkAsReady: boolean;
  markAsReadySent: boolean;
};

export type Dispatcher = {
  id: number;
  firstName: string;
  lastName: string;
  phone: {
    nationalNumber: string;
    number: string;
  };
  role: UserRole;
  createdAt: string;
};

export type VDriveDeliveryInternalNoteEntity = {
  note: string;
  createdAt: string;
  user: SimpleUserEntity;
};

export type DriverRating = {
  rating: number;
  comment?: string;
};

export enum VDriveSource {
  firstdelivery = 'firstdelivery',
  cartwheel = 'cartwheel',
  motoclick = 'motoclick',
  nash = 'nash',
  vdriveFleet = 'vdriveFleet',
}

export type SimpleVDriveDeliveryEntity = Pick<
  VDriveDeliveryEntity,
  | 'id'
  | 'orderId'
  | 'dspId'
  | 'dspCustomerId'
  | 'provider'
  | 'status'
  | 'charges'
  | 'vDriveSource'
> & {
  pickupAt: string | null;
  dropoffAt: string | null;
  driverName: string | null;
  driverPhone: string | null;
  customerName: string;
  customerPhone: string;
  address: string;
  storeAddress: string;
  storeName: string;
  storeNumber: string | null;
  tips: number;
  dspFee: number;
  deliveryFee: number;
  totalAmount: number;
  /** As `charges.debtRepayment`: not part of `totalAmount`. */
  debtRepayment?: number;
  creationType: DeliveryCreationType;
  timeZone: string;
  brand: SimpleBrandEntity;
  dispatcher?: Dispatcher | null;
  subTotal: number;
  interceptorCallId?: string | null;
  hasCallRecording?: boolean;
  cancellationReason?: string | null;
  interceptorCallStatus?: VDriveCallStatus | null;
  interceptorCallResultFailed?: boolean | null;
  driverRating?: DriverRating | null;
  distanceMiles: number;
  /** What the DSP reported; admin only, display only. */
  dspDistanceMiles?: number | null;
};

export type CreateManualDeliveryAddressData = {
  address: string;
  city: string;
  country: string;
  state: string;
  zipCode: string;
  coordinates: Coordinates;
  streetNumber: string;
};

export type VDriveGetRecordingResponse = {
  recordingUrl: string;
  inspectorUrl: string;
  transcription: VDriveCallTranscriptionResponse[];
  failed: boolean;
  status: VDriveCallStatus | null;
  finishReason: string | null;
  failDetails: string | null;
};

export type VDriveCallTranscriptionResponse = {
  text: string;
  speaker: Speaker;
  startTime: string;
  endTime: string;
};

export enum VDriveCallStatus {
  completed = 'completed',
  failed = 'failed',
  cancelled = 'cancelled',
  pending = 'pending',
  created = 'created',
  unknown = 'unknown',
  running = 'running',
  queued = 'queued',
}

export type Speaker = 'ai' | 'human';

export type VDriveGetQuoteHistoryResponse = {
  attempt: number;
  history: OrderVDriveQuoteHistoryEntity[];
  createdAt: string;
};

export type OrderVDriveQuoteHistoryEntity = {
  /**
   * Why this source gave no quote. Swagger types it as a string, but the
   * vDrive fleet spec reads the reason from `error.message`, so both shapes
   * are accepted - see `renderQuoteHistoryError`.
   */
  error?: string | { message?: string | null } | null;
  quote?: VDriveOffer | null;
  status: 'success' | 'error';
  attempt: number;
  provider: VDriveProvider;
  source: VDriveSource;
};

export type GetOrderPosTypeResponse = {
  posType: PosType;
};
